import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Globe, ArrowUpDown, ArrowRight, Search, Calculator, Layers,
  AlertTriangle, Receipt, CalendarClock, TrendingUp, BookOpen, Landmark,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { fmtPct, fmtMoney, fmtAum, fmtDate } from '@/components/coveredCall/ccFormat';
import WithholdingCalculator from '@/components/coveredCall/WithholdingCalculator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Foreign / ADR dividend page — /foreign-dividend-stocks
 *
 * ADRs are excluded from the sector pages (companies-only, US common
 * equity), so this is their home. The differentiator: withholding tax.
 * Every other site quotes the gross yield; we show what actually lands
 * in a US taxable account after the issuer country takes its cut.
 */
const COLUMNS = [
  { key: 'ticker', label: 'Company', align: 'left' },
  { key: 'country', label: 'Country', align: 'left' },
  { key: 'dividend_yield', label: 'Gross yield' },
  { key: 'withholding_rate', label: 'Withheld' },
  { key: 'net_yield', label: 'Net yield', hint: 'Yield after the issuer country withholds' },
  { key: 'frequency', label: 'Pays', align: 'left' },
  { key: 'payout_ratio', label: 'Payout' },
  { key: 'quality_score', label: 'Quality' },
  { key: 'market_cap', label: 'Size' },
  { key: 'perf_1y', label: '1Y' },
];

export default function ForeignDividendStocks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('all');
  const [iraFit, setIraFit] = useState('all');
  const [sort, setSort] = useState({ key: 'market_cap', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/foreign-dividend-stocks`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useSeoMeta({
    title: 'Foreign dividend stocks & ADRs — yields after withholding tax | DiviDrip',
    description: 'Every dividend-paying ADR ranked by gross yield AND net yield after foreign withholding tax, with the treaty rate for each country. Shell, Rio Tinto, Novo Nordisk, BTI and 160+ more.',
    canonical: 'https://twylightcrow.com/foreign-dividend-stocks',
  });

  const rows = data?.rows || [];
  const s = data?.stats;
  const hasCanada = rows.some((r) => r.country === 'Canada');

  const regions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.region) m.set(r.region, (m.get(r.region) || 0) + 1); });
    return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toUpperCase();
    const out = rows.filter((r) => {
      if (region !== 'all' && r.region !== region) return false;
      if (iraFit === 'ok' && r.ira_warning !== 'ok') return false;
      if (iraFit === 'not_avoid' && r.ira_warning === 'avoid') return false;
      if (!needle) return true;
      return r.ticker.includes(needle)
        || (r.name || '').toUpperCase().includes(needle)
        || (r.country || '').toUpperCase().includes(needle);
    });
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (['ticker', 'country', 'frequency'].includes(key)) {
        return String(av || '').localeCompare(String(bv || '')) * mult;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * mult;
    });
  }, [rows, q, region, iraFit, sort]);

  const faqs = useMemo(() => (s ? buildFaqs(s, data) : []), [s, data]);

  useEffect(() => {
    if (!data) return;
    const block = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.text = JSON.stringify(block);
    el.setAttribute('data-testid', 'foreign-jsonld');
    document.head.appendChild(el);
    return () => el.remove();
  }, [data, faqs]);

  const toggleSort = (key) => setSort((st) => (
    st.key === key ? { key, dir: st.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: ['ticker', 'country', 'frequency'].includes(key) ? 'asc' : 'desc' }
  ));

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 space-y-4 animate-pulse" data-testid="foreign-loading">
        <div className="h-10 w-2/3 bg-slate-200 dark:bg-slate-800 rounded" />
        <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="foreign-error">
        <BackOrHomeButton />
        <h1 className="mt-6 text-3xl font-black text-slate-900 dark:text-slate-100">Data unavailable</h1>
        <SiteDisclaimer />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="foreign-page">
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-400 font-semibold">
        <Globe className="w-3.5 h-3.5" />
        <Link to="/dividend-stocks-by-sector" className="hover:underline" data-testid="foreign-breadcrumb">Income research</Link>
      </div>

      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        Foreign dividend stocks, <span className="text-cyan-600 dark:text-cyan-400">after tax</span>
      </h1>
      <p className="mt-4 max-w-3xl text-base text-slate-600 dark:text-slate-400 leading-relaxed">
        A 5% yield from a Swiss company is not a 5% yield — Switzerland withholds 35% at source.
        This is every dividend-paying ADR we track with both the headline yield and the
        <strong className="text-slate-900 dark:text-slate-100"> net yield after the issuer
        country's withholding tax</strong>, so you can compare Shell against Rio Tinto against
        Novo Nordisk on the number that actually reaches your account.
      </p>
      <p className="mt-3 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
        {s.count} ADRs across {s.countries} countries, median gross yield{' '}
        <strong className="text-slate-900 dark:text-slate-100">{fmtPct(s.median_yield)}</strong>.
        {data.as_of && <> Prices as of {fmtDate(data.as_of)}.</>}
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="foreign-stats">
        <Stat label="ADR payers" value={s.count} testId="foreign-stat-count" />
        <Stat label="Median gross yield" value={fmtPct(s.median_yield)} testId="foreign-stat-yield" />
        <Stat label="No withholding at all" value={`${s.zero_withholding} of ${s.count}`} hint="UK, Ireland, Brazil, Singapore, Hong Kong" testId="foreign-stat-zero" />
        <Stat label="Yielding 4%+" value={`${s.yield_4plus} of ${s.count}`} testId="foreign-stat-high" />
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400" data-testid="foreign-substats">
        <Pill>{s.semi_annual} pay twice a year</Pill>
        <Pill>{s.annual} pay once a year</Pill>
        <Pill>{s.quarterly} pay quarterly</Pill>
        {!!s.monthly && <Pill>{s.monthly} pay monthly</Pill>}
        <Pill tone="warn">{s.reclaimable} withhold above the treaty rate (reclaimable)</Pill>
      </div>

      {/* Cuts */}
      <WithholdingCalculator rows={rows} />

      <section className="mt-12 grid md:grid-cols-2 gap-4 min-w-0" data-testid="foreign-cuts">
        <CutCard
          icon={TrendingUp}
          title="Highest gross yield"
          rows={data.cuts.highest_yield}
          metric={(r) => fmtPct(r.dividend_yield)}
          warn
          testId="foreign-cut-gross"
        />
        <CutCard
          icon={Receipt}
          title="Highest yield after withholding tax"
          rows={data.cuts.best_net_yield}
          metric={(r) => `${fmtPct(r.net_yield)} net`}
          warn
          testId="foreign-cut-net"
        />
        <CutCard
          icon={Globe}
          title="Zero withholding tax"
          rows={data.cuts.no_withholding}
          metric={(r) => `${fmtPct(r.dividend_yield)} · ${r.country}`}
          testId="foreign-cut-zero"
        />
        <CutCard
          icon={CalendarClock}
          title="Quarterly payers (rare for ADRs)"
          rows={data.cuts.quarterly_payers}
          metric={(r) => fmtPct(r.dividend_yield)}
          testId="foreign-cut-quarterly"
        />
      </section>

      {/* Country / withholding table */}
      <section className="mt-12" data-testid="foreign-countries">
        <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
          Withholding tax by country
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Standard treaty treatment for a US resident holding in a taxable account. Where the
          country withholds more than the treaty rate, the excess is reclaimable — and a US
          foreign tax credit usually offsets the rest.
        </p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm" data-testid="foreign-country-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Country</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Payers</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Median yield</th>
                <th className="px-3 py-2.5 text-right text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Withheld</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.countries.map((c) => (
                <tr key={c.country} className="border-t border-slate-100 dark:border-slate-800/70" data-testid={`foreign-country-${c.country.replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="px-3 py-2.5 font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                    {c.country}
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">{c.region}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400">{c.count}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">{fmtPct(c.median_yield)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={`px-2 py-0.5 rounded-md font-bold ${c.withholding_rate === 0 ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400' : c.withholding_rate >= 25 ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                      {c.withholding_rate == null ? '—' : `${c.withholding_rate}%`}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400">{c.withholding_note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Rates shown are the treaty rate a US resident should end up paying, with the higher
          at-source rate noted where one applies — general guidance only, not tax advice. Two things
          the table can't show you: the foreign tax credit only offsets US tax, so inside an IRA or
          Roth the withholding is a permanent leak (Canada is the one exception — 0% on Canadian
          dividends held in a retirement account), and ADR pass-through fees of roughly $0.01-$0.05
          per share are deducted on top. Both are covered in{' '}
          <Link to="/learn/international-dividends" className="text-cyan-700 dark:text-cyan-400 font-semibold hover:underline">the ADR withholding guide</Link>.
        </p>
      </section>

      {/* Main table */}
      <section className="mt-12" data-testid="foreign-table-section">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">All {s.count} dividend-paying ADRs</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{visible.length} shown · click any column to sort</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ticker, name or country…"
              className="pl-9 pr-3 py-2 w-64 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="foreign-search"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="foreign-region-filters">
          <Chip label="All regions" active={region === 'all'} onClick={() => setRegion('all')} testId="foreign-region-all" />
          {regions.map((r) => (
            <Chip
              key={r.name}
              label={`${r.name} (${r.count})`}
              active={region === r.name}
              onClick={() => setRegion(r.name)}
              testId={`foreign-region-${r.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
            />
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="foreign-ira-filters">
          <span className="text-xs font-bold text-slate-700 dark:text-slate-300 mr-1">Account fit:</span>
          <Chip label="Any account" active={iraFit === 'all'} onClick={() => setIraFit('all')} testId="foreign-ira-filter-all" />
          <Chip label={`Safe in my IRA / Roth (${s.ira_ok})`} active={iraFit === 'ok'} onClick={() => setIraFit('ok')} testId="foreign-ira-filter-ok" />
          <Chip label={`Hide "bad in IRA" (${s.count - s.ira_avoid})`} active={iraFit === 'not_avoid'} onClick={() => setIraFit('not_avoid')} testId="foreign-ira-filter-not-avoid" />
          {iraFit === 'ok' && (
            <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="foreign-ira-filter-note">
              Only countries that withhold nothing (or Canada, whose treaty waives it for retirement accounts) — nothing leaks in a Roth or IRA.
            </span>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-start gap-x-4 gap-y-2 text-xs" data-testid="foreign-ira-legend">
          <span className="font-bold text-slate-700 dark:text-slate-300">IRA suitability:</span>
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400">IRA ok</span>
            <span className="text-slate-600 dark:text-slate-400">
              Nothing withheld ({s.ira_ok}){hasCanada ? ' — or Canada, where the treaty waives it for retirement accounts' : ''}
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400">IRA leak</span>
            <span className="text-slate-600 dark:text-slate-400">Under 25% withheld and unrecoverable in an IRA</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400">bad in IRA</span>
            <span className="text-slate-600 dark:text-slate-400">25%+ withheld at source and permanently lost ({s.ira_avoid})</span>
          </span>
        </div>
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
          Why: foreign withholding is recovered through the US foreign tax credit, which only
          offsets US tax you actually owe. An IRA or Roth owes no US tax on the dividend, so there
          is nothing for the credit to offset and the withheld amount is gone for good — there is
          no reclaim route either. Hover any badge for the country-specific reason.
        </p>

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm" data-testid="foreign-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    title={c.hint}
                    onClick={() => toggleSort(c.key)}
                    className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none hover:text-cyan-600 dark:hover:text-cyan-400 whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right'}`}
                    data-testid={`foreign-th-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sort.key === c.key && <ArrowUpDown className="w-3 h-3 text-cyan-500" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.ticker} className="border-t border-slate-100 dark:border-slate-800/70 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`foreign-row-${r.ticker}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/stock/${r.ticker}`} className="font-mono font-bold text-cyan-700 dark:text-cyan-400 hover:underline">{r.ticker}</Link>
                      {r.ira_warning === 'avoid' && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 cursor-help" title={r.ira_note} data-testid={`foreign-ira-avoid-${r.ticker}`}>
                          bad in IRA
                        </span>
                      )}
                      {r.ira_warning === 'caution' && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 cursor-help" title={r.ira_note} data-testid={`foreign-ira-caution-${r.ticker}`}>
                          IRA leak
                        </span>
                      )}
                      {r.ira_warning === 'ok' && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 cursor-help" title={r.ira_note} data-testid={`foreign-ira-ok-${r.ticker}`}>
                          IRA ok
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px]">{r.name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.country || '—'}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    {fmtPct(r.dividend_yield)}
                    {r.yield_suspect && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-500" title="Unusually high — often a special or one-off payment in the trailing rate" />}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">
                    {r.withholding_rate == null ? '—' : `${r.withholding_rate}%`}
                    {r.withholding_reclaim && <span className="ml-1 text-[9px] uppercase font-bold text-amber-600 dark:text-amber-400" title={r.withholding_note}>rec</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums text-slate-900 dark:text-slate-100 whitespace-nowrap">{fmtPct(r.net_yield)}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.frequency || '—'}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtPct(r.payout_ratio, 0)}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.quality_score != null ? (
                      <span className={`px-2 py-0.5 rounded-md font-bold ${r.quality_score >= 70 ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400' : r.quality_score >= 40 ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>{r.quality_score}</span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-400 whitespace-nowrap">{fmtAum(r.market_cap)}</td>
                  <td className={`px-3 py-2.5 text-right whitespace-nowrap ${r.perf_1y == null ? 'text-slate-400' : r.perf_1y < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{fmtPct(r.perf_1y, 1, true)}</td>
                </tr>
              ))}
              {!visible.length && (
                <tr><td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500" data-testid="foreign-empty">No ADRs match that filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Net yield applies the treaty withholding rate to the current gross yield. “REC” marks
          countries that withhold above the treaty rate, where the excess is reclaimable. Issuer
          country is resolved from our own mapping — the exchange feed only records the ADR's
          listing country. {s.count - s.country_resolved} ADRs are still unmapped and show a dash.
        </p>
      </section>

      <section className="mt-12 p-6 rounded-xl bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="foreign-cta">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Keep going</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/learn/international-dividends" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-lg" data-testid="foreign-cta-guide-intl">
            <BookOpen className="w-4 h-4" /> ADRs, withholding tax & the foreign tax credit
          </Link>
          <Link to="/canadian-dividend-stocks" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="foreign-cta-canada">
            <Landmark className="w-4 h-4" /> Canadian cross-listed payers
          </Link>
          <Link to="/learn/foreign-ordinary-shares-tax" className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-300 dark:border-cyan-800 bg-cyan-50/60 dark:bg-cyan-950/20 hover:border-cyan-500 font-semibold rounded-lg" data-testid="foreign-cta-guide-ords">
            <BookOpen className="w-4 h-4" /> Foreign ordinary shares — the 2026 tax guide
          </Link>
          <Link to="/dividend-stocks-by-sector" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="foreign-cta-sectors">
            <Layers className="w-4 h-4" /> US dividend stocks by sector
          </Link>
          <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="foreign-cta-calc">
            <Calculator className="w-4 h-4" /> Project the income
          </Link>
          <Link to="/screener" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="foreign-cta-screener">
            <Search className="w-4 h-4" /> Screener
          </Link>
        </div>
        <p className="mt-3 text-xs text-slate-600 dark:text-slate-400">
          Both guides go deeper than this table: which reclaim forms actually work, why the foreign
          tax credit is useless inside an IRA, the Canada–IRA exception, ADR pass-through fees, and
          when a foreign dividend fails the qualified-dividend test.
        </p>
      </section>

      <section className="mt-12" data-testid="foreign-faq">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`foreign-faq-item-${i}`}>
              <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
                <span>{f.q}</span>
                <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function Stat({ label, value, hint, testId }) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-slate-100 truncate">{value ?? '—'}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}

function Pill({ children, tone }) {
  return (
    <span className={`px-2.5 py-1 rounded-full border ${tone === 'warn'
      ? 'border-amber-300 dark:border-amber-900 text-amber-700 dark:text-amber-400'
      : 'border-slate-200 dark:border-slate-800'}`}>{children}</span>
  );
}

function Chip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active
        ? 'bg-cyan-600 border-cyan-600 text-white'
        : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-cyan-400'}`}
      data-testid={testId}
    >{label}</button>
  );
}

function CutCard({ icon: Icon, title, rows, metric, warn, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 min-w-0 overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
        <Icon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" /> {title}
      </div>
      <ol className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.ticker} className="flex items-center justify-between gap-3 text-sm min-w-0">
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] tabular-nums text-slate-400 w-4 shrink-0">{i + 1}</span>
              <Link to={`/stock/${r.ticker}`} className="font-mono font-bold text-cyan-700 dark:text-cyan-400 hover:underline shrink-0">{r.ticker}</Link>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{r.name}</span>
            </span>
            <span className="shrink-0 tabular-nums font-semibold text-slate-800 dark:text-slate-200">
              {metric(r)}
              {warn && r.yield_suspect && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-500" />}
            </span>
          </li>
        ))}
      </ol>
      {warn && rows.some((r) => r.yield_suspect) && (
        <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-400 flex gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          Flagged yields are unusually high — normally a special or one-off payment still in the
          trailing rate. Many foreign companies pay a variable dividend tied to profits.
        </p>
      )}
    </div>
  );
}

function buildFaqs(s, data) {
  const zero = (data.countries || []).filter((c) => c.withholding_rate === 0).map((c) => c.country);
  return [
    {
      q: 'Do I pay foreign tax on ADR dividends?',
      a: `Usually yes. The issuer's home country withholds tax before the dividend reaches you — 10% for Japan under the treaty, 35% at source for Switzerland (15% treaty rate, reclaimable via Form 82), 26.375% for Germany — while ${zero.length ? zero.join(', ') : 'a handful of countries'} withhold nothing. In a US taxable account the foreign tax credit generally offsets what was withheld; inside an IRA it cannot be recovered at all, which is the single biggest mistake investors make with foreign dividend stocks.`,
    },
    {
      q: 'Which foreign dividend stocks have no withholding tax?',
      a: `${s.zero_withholding} of the ${s.count} ADRs we track come from countries that withhold nothing on dividends — principally the UK, Ireland, Brazil, Singapore and Hong Kong. That makes UK names like Shell, BTI, Unilever and National Grid, and Brazilian names like Petrobras and Vale, unusually efficient for income investors.`,
    },
    {
      q: 'What is the difference between gross yield and net yield here?',
      a: 'Gross yield is the headline figure every site quotes. Net yield applies the treaty withholding rate for the issuer country, so it approximates what actually lands in a US taxable account before US tax. A 6% Swiss yield nets 5.1% at the 15% treaty rate — but only 3.9% until the 20% excess above the treaty rate is reclaimed with Form 82, which is why the notes column matters as much as the number.',
    },
    {
      q: 'Why do so many ADRs only pay twice a year?',
      a: `Most of the world pays an interim and a final dividend rather than four quarterly cheques — ${s.semi_annual} of these ADRs pay semi-annually and ${s.annual} pay just once a year, versus only ${s.quarterly} that pay quarterly. If you're building a monthly income ladder, foreign payers cluster awkwardly, which is worth planning around.`,
    },
    {
      q: 'Are ADR fees charged on top of withholding tax?',
      a: 'Often, yes. Depositary banks charge a custody or pass-through fee, typically $0.01–$0.05 per share per year, deducted from the dividend or billed by your broker. It is small relative to withholding tax but it does mean the true net yield is slightly below the figure shown here.',
    },
    {
      q: 'How often is this list updated?',
      a: `Nightly, from our own data pipeline${data.as_of ? `, and this page reflects prices as of ${fmtDate(data.as_of)}` : ''}. Withholding rates are the standard treaty rates and change rarely, but they are general guidance rather than tax advice.`,
    },
  ];
}
